import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";

const ADVISOR = {
  initials: "DJ",
  name: "Dr. Deepak Joshi",
  role: "Scientific Advisor and Mentor",
  linkedin: "https://www.linkedin.com/in/deepak-joshi-831197101/",
  affiliations: [
    "Associate Professor, Centre for Biomedical Engineering, IIT Delhi",
    "Adjunct Faculty, AIIMS Delhi",
  ],
};

const TEAM = [
  { initials: "DH", name: "Dr. Doli Hazarika", role: "Co-founder & Chief Scientific Officer", linkedin: "https://www.linkedin.com/in/doli-hazarika-551053108/" },
  { initials: "MD", name: "Meghali Deka", role: "Chief Technology Officer", linkedin: "https://www.linkedin.com/in/meghali-d-530489135/" },
  { initials: "PB", name: "Priyal J. Baisakhiya", role: "Head, Product and Experience Design", linkedin: "https://www.linkedin.com/in/priyal-baisakhiya/" },
  { initials: "BS", name: "Biplab Sangma", role: "Co-founder & Chief Executive Officer", linkedin: "https://www.linkedin.com/in/biplabsangma/" },
];

const reveal = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
};

const PHOTOS = {
  DJ: "/assets/team/deepak-joshi.webp",
  DH: "/assets/team/doli-hazarika.webp",
  MD: "/assets/team/meghali-deka.webp",
  PB: "/assets/team/priyal-baisakhiya.webp",
  BS: "/assets/team/biplab-sangma.webp",
};

const NAMES = {
  DJ: "Dr. Deepak Joshi",
  DH: "Dr. Doli Hazarika",
  MD: "Meghali Deka",
  PB: "Priyal J. Baisakhiya",
  BS: "Biplab Sangma",
};

const Avatar = ({ initials, large, testId }) => (
  <div
    className={`relative rounded-full border border-[#10B8B1]/40 bg-gradient-to-br from-[#0e0c42] to-[#050422] flex items-center justify-center shadow-[0_0_30px_rgba(16,184,177,0.15)] overflow-hidden ${
      large ? "w-28 h-28" : "w-20 h-20"
    }`}
    data-testid={testId}
  >
    {PHOTOS[initials] && (
      <img
        src={PHOTOS[initials]}
        alt={NAMES[initials]}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
    )}
    {!PHOTOS[initials] && (
      <span className={`font-heading font-bold text-[#10B8B1] ${large ? "text-3xl" : "text-xl"}`}>{initials}</span>
    )}
    <div className="absolute inset-1.5 rounded-full border border-dashed border-[#10B8B1]/25 pointer-events-none" />
  </div>
);

const Team = () => (
  <section id="team" className="py-24 md:py-32 px-6 md:px-16 lg:px-24" data-testid="team-section">
    <div className="max-w-7xl mx-auto">
      <motion.div {...reveal} className="relative grid lg:grid-cols-12 gap-10 items-center qelp-card rounded-3xl p-8 md:p-12" data-testid="advisor-card">
        <a
          href={ADVISOR.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${ADVISOR.name} on LinkedIn`}
          className="absolute top-6 right-6 w-9 h-9 rounded-full border border-[#10B8B1]/30 bg-[#10B8B1]/5 flex items-center justify-center text-[#10B8B1] hover:bg-[#10B8B1] hover:text-[#050422] transition-colors duration-300"
          data-testid="advisor-linkedin"
        >
          <Linkedin size={15} />
        </a>
        <div className="lg:col-span-3 flex lg:justify-center">
          <Avatar initials={ADVISOR.initials} large testId="advisor-photo" />
        </div>
        <div className="lg:col-span-9">
          <p className="font-mono-qelp text-xs tracking-[0.3em] uppercase text-[#10B8B1]" data-testid="advisor-overline">
            Guidance from established scientific and clinical expertise
          </p>
          <h3 className="mt-4 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-100" data-testid="advisor-name">
            {ADVISOR.name}
          </h3>
          <p className="mt-1.5 text-sm text-slate-300">{ADVISOR.role}</p>
          <div className="mt-4 space-y-1.5">
            {ADVISOR.affiliations.map((a) => (
              <p key={a} className="font-mono-qelp text-[11px] tracking-[0.1em] text-slate-500">{a}</p>
            ))}
          </div>
        </div>
      </motion.div>

      <motion.div {...reveal} className="mt-24">
        <div className="flex items-center gap-5" data-testid="team-overline">
          <span className="font-mono-qelp text-[10px] tracking-[0.3em] uppercase text-[#10B8B1]">03</span>
          <span className="flex-1 h-px bg-white/10" />
          <span className="font-mono-qelp text-[10px] tracking-[0.3em] uppercase text-slate-600">The People</span>
        </div>
        <h2 className="mt-5 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100" data-testid="team-heading">
          The people building Qelp AI.
        </h2>
      </motion.div>

      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {TEAM.map((m, i) => (
          <motion.div
            key={m.name}
            {...reveal}
            transition={{ ...reveal.transition, delay: i * 0.08 }}
            className="qelp-card relative rounded-3xl p-7 flex flex-col items-start gap-5"
            data-testid={`team-member-${i}`}
          >
            <a
              href={m.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${m.name} on LinkedIn`}
              className="absolute top-5 right-5 w-9 h-9 rounded-full border border-[#10B8B1]/30 bg-[#10B8B1]/5 flex items-center justify-center text-[#10B8B1] hover:bg-[#10B8B1] hover:text-[#050422] transition-colors duration-300"
              data-testid={`team-linkedin-${i}`}
            >
              <Linkedin size={15} />
            </a>
            <Avatar initials={m.initials} testId={`team-photo-${i}`} />
            <div>
              <h3 className="font-heading text-base sm:text-lg font-semibold text-white">{m.name}</h3>
              <p className="mt-1.5 font-mono-qelp text-[10px] tracking-[0.15em] uppercase text-slate-500 leading-relaxed">
                {m.role}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default Team;
